import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Layers, ExternalLink } from 'lucide-react';
import { cn } from '../../lib/utils';

export function DraggablePin({
  pin,
  Icon,
  color,
  language,
  isMJ,
  editMode,
  onPinClick,
  onPinDoubleClick,
  onPinDragEnd,
  onNavigateToLinkedMap
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [isLongPress, setIsLongPress] = useState(false);
  const pinRef = useRef(null);
  const startPosRef = useRef({ x: 0, y: 0 });
  const hasMovedRef = useRef(false);
  
  const hasLinkedMap = !!pin.linked_map_id;

  // Mouse drag (edit mode only)
  const handleMouseDown = useCallback((e) => {
    if (!editMode || !isMJ) return;
    
    e.stopPropagation();
    e.preventDefault();
    
    startPosRef.current = { x: e.clientX, y: e.clientY };
    hasMovedRef.current = false;
    
    const handleMouseMove = (moveEvent) => {
      const dx = moveEvent.clientX - startPosRef.current.x;
      const dy = moveEvent.clientY - startPosRef.current.y;
      
      // Start dragging after moving more than 5 pixels
      if (!isDragging && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) {
        setIsDragging(true);
        hasMovedRef.current = true;
      }
      
      if (hasMovedRef.current) {
        setDragOffset({ x: dx, y: dy });
      }
    };
    
    const handleMouseUp = (upEvent) => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      
      if (hasMovedRef.current && isDragging) {
        // Calculate new position based on parent container
        const container = pinRef.current?.closest('.map-pins-container');
        if (container) {
          const rect = container.getBoundingClientRect();
          const newX = (upEvent.clientX - rect.left) / rect.width;
          const newY = (upEvent.clientY - rect.top) / rect.height;
          
          // Clamp to 0-1 range
          const clampedX = Math.max(0, Math.min(1, newX));
          const clampedY = Math.max(0, Math.min(1, newY));
          
          onPinDragEnd(pin.id, clampedX, clampedY);
        }
      }
      
      setIsDragging(false);
      setDragOffset({ x: 0, y: 0 });
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [editMode, isMJ, isDragging, pin.id, onPinDragEnd]);

  // Touch handling (long press to drag)
  const handleTouchStart = useCallback((e) => {
    if (!editMode || !isMJ) return;
    
    const touch = e.touches[0];
    startPosRef.current = { x: touch.clientX, y: touch.clientY };
    hasMovedRef.current = false;
    setIsLongPress(false);
    
    // Set up long press timer (500ms)
    const timer = setTimeout(() => {
      setIsLongPress(true);
      // Vibrate if available
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
    }, 500);
    
    setLongPressTimer(timer);
  }, [editMode, isMJ]);

  const handleTouchMove = useCallback((e) => {
    if (!editMode || !isMJ) return;
    
    const touch = e.touches[0];
    const dx = touch.clientX - startPosRef.current.x;
    const dy = touch.clientY - startPosRef.current.y;
    
    // Cancel long press if moved before timer
    if (!isLongPress && (Math.abs(dx) > 10 || Math.abs(dy) > 10)) {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        setLongPressTimer(null);
      }
      return;
    }
    
    // If long press is active, start dragging
    if (isLongPress) {
      e.preventDefault();
      hasMovedRef.current = true;
      setIsDragging(true);
      setDragOffset({ x: dx, y: dy });
    }
  }, [editMode, isMJ, isLongPress, longPressTimer]);

  const handleTouchEnd = useCallback((e) => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
    
    if (hasMovedRef.current && isDragging) {
      const touch = e.changedTouches[0];
      const container = pinRef.current?.closest('.map-pins-container');
      if (container) {
        const rect = container.getBoundingClientRect();
        const newX = (touch.clientX - rect.left) / rect.width;
        const newY = (touch.clientY - rect.top) / rect.height;
        
        const clampedX = Math.max(0, Math.min(1, newX));
        const clampedY = Math.max(0, Math.min(1, newY));
        
        onPinDragEnd(pin.id, clampedX, clampedY);
      }
    }
    
    setIsDragging(false);
    setIsLongPress(false);
    setDragOffset({ x: 0, y: 0 });
  }, [isDragging, longPressTimer, pin.id, onPinDragEnd]);

  // Click handler
  const handleClick = (e) => {
    // Don't trigger click if we were dragging
    if (hasMovedRef.current) {
      e.stopPropagation();
      return;
    }
    onPinClick(pin, e);
  };

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
      }
    };
  }, [longPressTimer]);

  const [isHovered, setIsHovered] = useState(false);

  const handleLinkedMapClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (onNavigateToLinkedMap && pin.linked_map_id) {
      onNavigateToLinkedMap(pin.linked_map_id);
    }
  };

  return (
    <TooltipProvider>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          <div
            ref={pinRef}
            className={cn(
              "absolute transform -translate-x-1/2 -translate-y-full select-none group/pin",
              "transition-all hover:scale-125 hover:z-50",
              !pin.discovered && isMJ && "opacity-60",
              hasLinkedMap && "ring-2 ring-primary ring-offset-2 rounded-full",
              isDragging && "scale-125 z-[100] cursor-grabbing",
              editMode && isMJ && !isDragging && "cursor-grab",
              !editMode && "cursor-pointer"
            )}
            style={{
              left: `${pin.x * 100}%`,
              top: `${pin.y * 100}%`,
              transform: isDragging
                ? `translate(calc(-50% + ${dragOffset.x}px), calc(-100% + ${dragOffset.y}px)) scale(1.25)`
                : undefined,
              transition: isDragging ? 'none' : undefined
            }}
            onClick={handleClick}
            onDoubleClick={(e) => onPinDoubleClick(pin, e)}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            data-testid={`pin-${pin.id}`}
          >
            <div
              className={cn(
                "p-1.5 rounded-full shadow-lg",
                isDragging && "shadow-xl ring-2 ring-white"
              )}
              style={{ backgroundColor: color }}
            >
              <Icon className="h-4 w-4 text-white" />
            </div>
            <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs font-medium bg-background/90 px-1.5 py-0.5 rounded shadow flex items-center gap-1">
              {pin.title}
              {hasLinkedMap && <Layers className="inline h-3 w-3 text-primary" />}
            </div>

            {/* Go to linked map button — visible on hover */}
            {hasLinkedMap && isHovered && !isDragging && !editMode && (
              <button
                type="button"
                onClick={handleLinkedMapClick}
                className={cn(
                  "absolute -top-2 -right-2 z-[60]",
                  "flex items-center gap-1 px-1.5 py-0.5 rounded-full",
                  "bg-primary text-primary-foreground shadow-lg",
                  "text-[10px] font-medium whitespace-nowrap",
                  "animate-in fade-in zoom-in-95 duration-150",
                  "hover:bg-primary/90 active:scale-95 transition-transform"
                )}
                title={language === 'fr' ? 'Ouvrir la carte liée' : 'Open linked map'}
              >
                <ExternalLink className="h-3 w-3" />
                <span className="hidden sm:inline">{language === 'fr' ? 'Ouvrir' : 'Open'}</span>
              </button>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <p className="font-semibold">{pin.title}</p>
          {pin.public_description && (
            <p className="text-sm text-muted-foreground mt-1">{pin.public_description}</p>
          )}
          {hasLinkedMap && (
            <p className="text-xs text-primary mt-2 flex items-center gap-1">
              <ExternalLink className="h-3 w-3" />
              {language === 'fr' ? 'Cliquez le bouton pour ouvrir la carte' : 'Click the button to open the map'}
            </p>
          )}
          {editMode && isMJ && (
            <p className="text-xs text-muted-foreground mt-2 border-t pt-1">
              {language === 'fr' ? '📌 Glisser pour déplacer' : '📌 Drag to move'}
            </p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
